import React, { useMemo, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CycleTable from '../../AppGrid/CycleTable';
import strings from '../../Globalization/index';
import { useBreadcrumbs } from '../../contexts/Breadcrumbs';
import consts from '../../consts';
import { usePrerequisites } from '../../contexts/Prerequisites';


const DashboardCycleList = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [, setBreadcrumbs] = useBreadcrumbs();
	const [, { getLicenseType }] = usePrerequisites();
	const ref = useRef();
	const licenceType = typeof getLicenseType === 'function' ? getLicenseType() : {};
	const cyclesOnly = licenceType?.catexport && !licenceType?.multi;

	const handleOnSetActiveRecord = (activeRecord) => {
		const { id, record = {} } = activeRecord;
		let { EquipmentTypeId } = record;
		const {
			AdvantageCycleId = '',
			AttachmentCount,
			EquipmentFamilyId,
			EndoSystemCycleId,
			SterilizerCycleId,
			SterilizerId
		} = record;
		if (EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE3000) {
			EquipmentTypeId = EquipmentFamilyId;
		}
		if (!cyclesOnly && licenceType.catviewer) {
			let destPath;
			switch (EquipmentTypeId) {
				case consts.EquipmentType.AER:
					destPath = `/advantagecycleinfo?id=${AdvantageCycleId}&equipmentFamilyId=${EquipmentFamilyId}&attachmentCount=${AttachmentCount}`;
					break;
				case consts.EquipmentType.CABINET:
					destPath = `/endosystemcycleinfo?id=${EndoSystemCycleId}&attachmentCount=${AttachmentCount}&equipmentFamilyId=${EquipmentFamilyId}&isEndoCabinet=1`;
					break;
				case consts.EquipmentType.LIQUIDCHEMICAL:
					destPath = `/endosystemcycleinfo?id=${EndoSystemCycleId}&attachmentCount=${AttachmentCount}&equipmentFamilyId=${EquipmentFamilyId}&isLiquidChemical=1`;
					break;
				case consts.EquipmentType.STERILIZER:
				case consts.EquipmentType.STEAMSTERILIZER:
				case consts.EquipmentType.VHPSTERILIZER:
					destPath = `/sterilizercycleinfo?id=${SterilizerCycleId}&sterilizerId=${SterilizerId}&attachmentCount=${AttachmentCount}`;
					break;
				case consts.EquipmentType.INCUBATOR:
					destPath = (`/incubatorcycledetailform?id=${record?.IncubatorCycleDetailId || 0}&attachmentCount=${record?.AttachmentCount || 0}&cycleId=${record?.CycleId || 0}&incubatorCycleId=${record?.IncubatorCycleId || 0}&isError=${record?.BIResult === consts.BIResult.Error}&isFromCycle=${true}&incubatorId=${record?.IncubatorId}&returnTo=${encodeURIComponent('/cyclelist')}`);
					break;
				case consts.EquipmentFamilyType.ENSPIRE3000:
					destPath = `/enspirecycleinfo?id=${EndoSystemCycleId}&equipmentFamilyId=${EquipmentFamilyId}&equipmentId=${record.EquipmentId}&attachmentCount=${AttachmentCount}`;
					break;
				default:
					destPath = (`/cycleinfo?id=${id}`);
					break;
			}
			if (destPath) {
				navigate(destPath);
			}
		}
	};

	useEffect(() => {
		setBreadcrumbs({
			breadcrumbs: [{ text: strings.Cycle || 'Cycles' }],
			docTitle: strings.Cycles,
			subbreadcrumbs: [{ text: strings.Dashboard, to: '/dashboard' }, { text: strings.Cycle || 'Cycles' }]
		});
	}, [setBreadcrumbs]);

	const IsReviewed = Boolean(searchParams.get('IsReviewed'));
	const IsCycleId = searchParams.get('cycleStatusId');
	const locationId = searchParams.get('locationId');
	const equipmentTypeId = searchParams.get('equipmentTypeId');
	const parentFilters = useMemo(() => {
		const date = dayjs().hour(0).minute(0).second(0);
		const CycleStart = date.format(consts.MomentFormat.MonthDateYear);
		const utcDate = date.utc().millisecond(0).toISOString();
		const createReviewObject = (isReviewed) => {
			return {
				operator: "$in",
				value: isReviewed === '1' ? ["Pass", "Fail"] : [null],
				property: "ReviewStatus",
				type: "string"
			};
		};
		const createCycleStatusObject = (isCycleId) => {
			if (isCycleId === '1') {
				return {
					operator: "$in",
					value: ["1", "2", "110", "120", "121", "140", "150"],
					property: "CycleStatusId",
					type: "string"
				};
			} else if (isCycleId === '0') {
				return {
					operator: "$in",
					value: ["0", "3", "100", "101"],
					property: "CycleStatusId",
					type: "numeric"
				};
			} else {
				return {
					operator: "$eq",
					value: isCycleId,
					property: "CycleStatusId",
					type: "numeric"
				};
			}
		};
		const Review = createReviewObject(searchParams.get('IsReviewed'));
		const CycleStatus = createCycleStatusObject(IsCycleId);
		const cycleStartCondition = {
			"operator": "$gt",
			"value": utcDate,
			"property": "CycleStart",
			"type": "date",
			"convert": "true",
			"utcDate": utcDate
		};
		const EventCondition = { operator: '$not', property: 'EventType', value: '0', type: 'string' };
		let filters;
		if (IsCycleId === "-9999") {
			// Apply multiple filters when IsCycleId is -9999
			filters = [
				Review,
				cycleStartCondition,
				EventCondition
			];
		} else {
			// Create a single filter when IsCycleId is not -9999
			filters = [
				CycleStatus,
				cycleStartCondition,
				EventCondition
			];
		}
		return { filter: filters };
	}, [IsReviewed]);

	const apiParams = useMemo(() => {
		const obj = { "LocationId": locationId };
		const equipmentType = equipmentTypeId === '0' ? 'undefined' : equipmentTypeId;
		if (equipmentType !== 'undefined') {
			obj.EquipmentType = equipmentTypeId;
		}
		return obj;
	}, [locationId, equipmentTypeId])
	return (
		<Grid container>
			<Grid size={12}>
				<CycleTable
					canMutateLocation={false}
					onSetActiveRecord={handleOnSetActiveRecord}
					tableRef={ref}
					apiParams={apiParams}
					parentFilters={parentFilters}
				/>
			</Grid>
		</Grid>
	);
};

export default DashboardCycleList